import type { Dictionary } from './en.ts';

export const es: Dictionary = {
  common: {
    tagline: 'traductor de voz en tiempo real',
    interfaceLanguage: 'Idioma de la interfaz',
  },

  login: {
    title: 'Entrar',
    lede: 'Introduce el código de acceso a la demo. Este navegador lo recordará durante 12 horas.',
    submit: 'Entrar',
    checking: 'Comprobando…',
    wrong: 'El código no es correcto. Revísalo e inténtalo de nuevo.',
    failed: 'No se pudo entrar. Inténtalo dentro de un momento.',
  },

  home: {
    title: 'Una conversación en dos idiomas',
    lede: 'Cada uno habla su idioma y escucha al otro traducido. Dos personas por sala, sin registro.',
    myLanguage: 'Mi idioma',
    peerLanguage: 'Idioma de la otra persona',
    accessCode: 'Código de acceso a la demo',
    signOut: 'Salir',
    samePair: 'Elige dos idiomas distintos; si no, no hay nada que traducir.',
    create: 'Crear una sala',
    creating: 'Creando…',
    createFailed: 'No se pudo crear la sala',

    readyTitle: 'La sala está lista',
    youHear: (language: string) => `Tú escuchas: ${language}.`,
    theyHear: (language: string) => `La otra persona escucha: ${language}.`,
    inviteLabel: 'Enlace de invitación: envíaselo a la otra persona',
    copy: 'Copiar',
    copied: 'Copiado',
    inviteNote:
      'El enlace dura 15 minutos y deja entrar exactamente a una persona. No lo publiques: es la llave de la sala.',
    sourceSubtitles: 'Mostrar también las palabras originales',
    sourceSubtitlesOn:
      'Verás lo que cada persona dijo realmente, en su propio idioma, junto a la traducción. El proveedor lo cobra aparte, así que la llamada cuesta más.',
    sourceSubtitlesOff:
      'Solo se muestra la traducción. No se transcribe nada en el idioma original y no se cobra nada adicional.',
    sourceSubtitlesReady: 'También se mostrarán las palabras originales.',
    goToCheck: 'Comprobar el audio',
  },

  voice: {
    you: 'Tú',
    peer: 'La otra persona',
    language: 'Idioma',
    provider: 'Locución',
    openaiNote: 'voz integrada, suena al momento',
    elevenlabsNote: 'voz más limpia, empieza al terminar la frase',
    noVoices: 'no hay voces para este idioma',
    voice: 'Voz',
    builtIn: 'Voz integrada de OpenAI',
    loading: 'Cargando voces…',
    failed: 'No se pudieron cargar las voces. Inténtalo más tarde.',
    hintBuiltIn: 'Una sola voz para todos los idiomas, como antes.',
    hintLibrary: 'Una voz de la biblioteca de ElevenLabs.',
    groupVerified: 'Comprobadas para este idioma',
    groupOther: 'Otras voces',
    accent: 'Esta voz no está comprobada para este idioma; puede sonar con acento.',
    noSample: 'Esta voz no tiene muestra',
    listen: 'Escuchar',
    stop: 'Detener',
    needVoice: 'Elige una voz para cada lado con ElevenLabs o vuelve a poner OpenAI.',
  },

  join: {
    checking: 'Comprobando la invitación…',
    title: 'Invitación a una conversación',
    youWillHear: (language: string) => `Escucharás: ${language}.`,
    theyHear: (language: string) => `La otra persona escucha: ${language}.`,
    connect: 'Entrar',
    connecting: 'Entrando…',
    full: 'La sala está llena.',
    failedTitle: 'No se pudo entrar',
    createYourOwn: 'Crear tu propia sala',
    noInvite: 'Este enlace no lleva invitación. Pide que te lo manden de nuevo.',
    unavailable: 'La sala no está disponible',
    joinFailed: 'No se pudo entrar',
  },

  check: {
    title: 'Comprobación de audio',
    lede: 'Ponte auriculares: sin ellos la traducción vuelve a entrar por tu micrófono y la otra persona oye eco.',
    microphone: 'Micrófono',
    defaultMic: 'Micrófono predeterminado',
    level: 'Nivel: di algo',
    applied: (features: string) => `El navegador aplicó: ${features}.`,
    appliedNone: 'sin procesamiento',
    echoCancellation: 'cancelación de eco',
    noiseSuppression: 'supresión de ruido',
    autoGain: 'ganancia automática',
    measureNote: 'Haz las mediciones con auriculares.',
    testSound: 'Probar el sonido',
    testAgain: 'Probar el sonido otra vez',
    start: 'Todo bien, empezar',
    starting: 'Conectando…',
    micDenied:
      'Se denegó el acceso al micrófono. Permítelo en los ajustes del navegador y recarga la página.',
    micNotFound: 'No se encontró ningún micrófono. Conecta uno y recarga la página.',
    micFailed: 'No se pudo abrir el micrófono.',
  },

  call: {
    title: 'Conversación',
    timeLeft: (time: string) => `· quedan ${time}`,
    waitingPeer: 'Esperando a la otra persona. Envíale el enlace de invitación.',
    autoplayBlocked: 'El navegador bloqueó la reproducción automática.',
    enableSound: 'Activar el sonido',
    lagging:
      'La traducción va con retraso. Puedes reiniciar el traductor; se perderá el fragmento sin terminar.',
    translatorStopped: (reason: string) =>
      `La traducción se detuvo: ${reason}. La llamada continúa; reinicia el traductor.`,
    translationVolume: 'Volumen de la traducción',
    hearOriginal: 'Oír la voz original',
    muteMic: 'Apagar el micrófono',
    unmuteMic: 'Encender el micrófono',
    restart: 'Reiniciar la traducción',
    asleep:
      'Nadie ha hablado durante un minuto, así que la traducción se ha pausado para ahorrar costes. Se reanudará sola cuando hable tu interlocutor.',
    wake: 'Reanudar ahora',
    leave: 'Salir',
    peerMuted: 'La otra persona tiene el micrófono apagado',

    metricConnect: 'Arranque del traductor',
    metricFirst: 'Primera traducción',
    metricLag: 'Retraso estimado',
    metricRtt: 'RTT al proveedor',
    metricVoiceDelay: 'Retraso de la locución',
    metricCost: 'Coste hasta ahora (estimado)',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · créditos: ${credits}`,
    costNote: 'Precios públicos configurados en el servidor, no la factura de los proveedores.',
    metricVoiceLead: 'ElevenLabs frente a OpenAI, media',
    voiceLeadEarlier: (seconds: string) => `${seconds} s antes`,
    voiceLeadLater: (seconds: string) => `${seconds} s después`,
    iHear: (voice: string) => `Oigo: ${voice}`,
    peerHears: (voice: string) => `La otra persona oye: ${voice}`,
    voiceFellBack: 'OpenAI (ElevenLabs no respondió)',

    endedTitle: 'La conversación ha terminado',
    endedNewRoom: 'Para otra conversación, crea una sala nueva.',
    newRoom: 'Sala nueva',
    rateTitle: 'Valora la conversación',
  },

  closed: {
    'peer-left': 'La otra persona terminó la conversación.',
    'time-limit': 'Se agotó el tiempo de la demo.',
    'peer-lost': 'La otra persona estuvo desconectada demasiado tiempo.',
    'server-shutdown': 'El servidor se está reiniciando.',
    expired: 'La sala se cerró por inactividad.',
    unknown: 'Se cerró la conexión.',
    signalLost: 'Se perdió el contacto con el servidor. Crea una sala nueva.',
  },

  status: {
    serverConnecting: 'Servidor: conectando',
    serverOpen: 'Servidor: conectado',
    serverReconnecting: 'Servidor: reconectando',
    serverClosed: 'Servidor: desconectado',

    peerWaiting: 'La otra persona: esperando',
    peerConnecting: 'La otra persona: conectando',
    peerConnected: 'La otra persona: conectada',
    peerReconnecting: 'La otra persona: recuperando',
    peerFailed: 'La otra persona: sin conexión',
    peerGone: 'La otra persona: se fue',

    translatorIdle: 'Traducción para mí: sin iniciar',
    translatorConnecting: 'Traducción para mí: iniciando',
    translatorReady: 'Traducción para mí: funcionando',
    translatorReconnecting: 'Traducción para mí: recuperando',
    translatorFailed: 'Traducción para mí: error',
    translatorClosed: 'Traducción para mí: detenida',
    translatorAsleep: 'Traducción para mí: en pausa',

    peerTranslatorReady: 'Traducción para la otra persona: funcionando',
    peerTranslatorWaiting: 'Traducción para la otra persona: esperando',
    peerTranslatorAsleep: 'Traducción para la otra persona: en pausa',
    relayed: 'Ruta: por TURN',
  },

  subtitles: {
    them: 'La otra persona',
    you: 'Tú',
    themEmpty: 'Todavía no ha dicho nada',
    youEmpty: 'Di algo: aquí aparecerán tus palabras y su traducción',
  },

  rating: {
    accuracy: 'Precisión de la traducción',
    voice: 'Naturalidad de la voz',
    comfort: 'Comodidad de la conversación',
    send: 'Enviar las valoraciones',
    thanks: 'Gracias, valoraciones registradas.',
  },

  errors: {
    no_credit: 'la cuenta de OpenAI se quedó sin saldo; recárgala',
    rate_limited: 'el servicio de traducción está limitando las peticiones; inténtalo en un minuto',
    unconfigured: 'el servidor no tiene configurada la clave del servicio de traducción',
    unreachable: 'el servidor no pudo contactar con el servicio de traducción',
    offline: 'no se pudo contactar con el servidor; comprueba tu conexión',
    session_expired: 'la sesión de traducción caducó; reinicia la traducción',
    room_closed: 'esta sala ya está cerrada',
    bad_offer: 'el navegador generó una oferta de conexión inválida',
    provider_refused: (status: number) =>
      `el servicio de traducción rechazó la conexión (${status})`,
    unknown: 'no se pudo iniciar la traducción',
  },

  banner: {
    providerUnconfigured:
      'El servidor no tiene clave del proveedor de traducción. Puedes crear una sala, pero no se traducirá nada.',
  },
};
